import { mitigations } from '@/data/riskAssessment';
import type {
  AssessmentResult,
  RiskAssessment,
  RiskCategory,
  RiskLevel,
} from '@/types/riskAssessment';

export function calculateRiskLevel(score: number, maxScore: number): RiskLevel {
  const percentage = (score / maxScore) * 100;

  if (percentage <= 40) return 'low';
  if (percentage <= 70) return 'medium';
  return 'high';
}

export function calculateAssessmentResult(
  assessments: RiskAssessment[],
  categories: RiskCategory[]
): AssessmentResult {
  // Calculate total score
  const totalScore = assessments.reduce((sum, assessment) => sum + assessment.score, 0);
  const maxScore = categories.reduce(
    (sum, category) => sum + category.factors.length * 3, // Max 3 points per factor
    0
  );

  // Calculate category scores
  const categoryScores = categories.map(category => {
    const categoryAssessments = assessments.filter(a => a.categoryId === category.id);
    const categoryScore = categoryAssessments.reduce((sum, a) => sum + a.score, 0);
    const categoryMaxScore = category.factors.length * 3;

    return {
      categoryId: category.id,
      categoryName: category.name,
      score: categoryScore,
      maxScore: categoryMaxScore,
      riskLevel: calculateRiskLevel(categoryScore, categoryMaxScore),
    };
  });

  // Overall risk level
  const overallRiskLevel = calculateRiskLevel(totalScore, maxScore);

  // Get applicable mitigations
  const applicableMitigations = mitigations
    .filter(m => m.applicableFor.includes(overallRiskLevel))
    .sort((a, b) => {
      const priorityOrder = { high: 0, medium: 1, low: 2 };
      return priorityOrder[a.priority] - priorityOrder[b.priority];
    });

  return {
    overallRiskLevel,
    totalScore,
    maxScore,
    categoryScores,
    mitigations: applicableMitigations,
  };
}

export function getRiskLevelColor(riskLevel: RiskLevel): string {
  switch (riskLevel) {
    case 'low':
      return 'bg-green-100 text-green-800 border-green-300';
    case 'medium':
      return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    case 'high':
      return 'bg-red-100 text-red-800 border-red-300';
  }
}

export function getRiskLevelLabel(riskLevel: RiskLevel): string {
  switch (riskLevel) {
    case 'low':
      return 'Bajo';
    case 'medium':
      return 'Medio';
    case 'high':
      return 'Alto';
  }
}
